import React, { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  MessageCircle, 
  X, 
  Send, 
  CheckCircle, 
  Bug, 
  Sparkles, 
  Lightbulb,
  MoreHorizontal,
  Loader2
} from 'lucide-react';

const FEEDBACK_API_URL = 'https://api.threat.live/api/send-message.php';
// Anahtar gövdede gönderilir: özel X-API-Key başlığı CORS ön kontrolünde (preflight) reddediliyordu
// → tarayıcı isteği hiç göndermiyordu. (Anahtar zaten herkese açık JS'te; asıl koruma Turnstile + honeypot + rate limit.)
const FEEDBACK_API_KEY = 'RTBH_MESSAGE_API_KEY_2025';
const TURNSTILE_SITEKEY = '0x4AAAAAACLpdpidtZFYh3Ns';

const FeedbackWidget = () => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [honeypot, setHoneypot] = useState('');
  const turnstileRef = useRef(null);
  const widgetId = useRef(null);
  
  const [formData, setFormData] = useState({
    type: '',
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const translations = {
    tr: {
      feedbackButton: 'Geri Bildirim',
      title: 'Geri Bildirim Gönder',
      type: 'Tür',
      selectType: 'Seçiniz...',
      typeBug: 'Hata Bildirimi',
      typeFeature: 'Özellik Talebi',
      typeImprovement: 'İyileştirme Önerisi',
      typeOther: 'Diğer',
      name: 'Ad Soyad',
      namePlaceholder: 'Adınız ve soyadınız',
      email: 'E-posta',
      emailPlaceholder: 'ornek@sirket.com',
      subject: 'Konu',
      subjectPlaceholder: 'Kısa bir konu yazın...',
      message: 'Mesaj',
      messagePlaceholder: 'Detaylı açıklama yazın...',
      messageHelp: 'Maksimum 2000 karakter',
      cancel: 'İptal',
      send: 'Gönder',
      sending: 'Gönderiliyor...',
      successTitle: 'Geri Bildiriminiz Gönderildi!',
      successMessage: 'Değerli geri bildiriminiz için teşekkürler. En kısa sürede inceleyeceğiz.',
      close: 'Kapat',
      apiError: 'Gönderim sırasında bir hata oluştu. Lütfen tekrar deneyin veya contact@threat.live adresine yazın.',
      rateLimited: 'Çok fazla mesaj gönderildi. Lütfen birkaç dakika sonra tekrar deneyin.',
      verifyFailed: 'Doğrulama başarısız. Lütfen robot olmadığınızı doğrulayıp tekrar deneyin.',
    },
    en: {
      feedbackButton: 'Feedback',
      title: 'Send Feedback',
      type: 'Type',
      selectType: 'Select...',
      typeBug: 'Bug Report',
      typeFeature: 'Feature Request',
      typeImprovement: 'Improvement Suggestion',
      typeOther: 'Other',
      name: 'Full Name',
      namePlaceholder: 'Your full name',
      email: 'Email',
      emailPlaceholder: 'example@company.com',
      subject: 'Subject',
      subjectPlaceholder: 'Write a short subject...',
      message: 'Message',
      messagePlaceholder: 'Write a detailed description...',
      messageHelp: 'Maximum 2000 characters',
      cancel: 'Cancel',
      send: 'Send',
      sending: 'Sending...',
      successTitle: 'Feedback Sent!',
      successMessage: 'Thank you for your valuable feedback. We will review it as soon as possible.',
      close: 'Close',
      apiError: 'An error occurred while sending. Please try again or email contact@threat.live.',
      rateLimited: 'Too many messages sent. Please try again in a few minutes.',
      verifyFailed: 'Verification failed. Please confirm you are not a robot and try again.',
    }
  };

  const txt = translations[language] || translations.en;

  // Turnstile kutusu (form açıkken). Sunucuda gizli anahtar tanımlıysa jeton doğrulanır.
  useEffect(() => {
    if (!isOpen || isSuccess) return undefined;
    const render = () => {
      if (!window.turnstile || !turnstileRef.current || widgetId.current !== null) return;
      widgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITEKEY,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(null),
        'error-callback': () => setTurnstileToken(null),
        theme: 'auto',
        size: 'flexible',
      });
    };
    const timer = setInterval(() => { if (window.turnstile) { clearInterval(timer); render(); } }, 100);
    const stop = setTimeout(() => clearInterval(timer), 10000);
    return () => {
      clearInterval(timer);
      clearTimeout(stop);
      if (widgetId.current !== null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch (e) { /* zaten kaldırılmış */ }
      }
      widgetId.current = null;
      setTurnstileToken(null);
    };
  }, [isOpen, isSuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setIsSubmitting(true);

    try {
      // Content-Type dışında özel başlık yok → preflight .htaccess'in izin verdiği başlıklarla geçer
      const response = await fetch(FEEDBACK_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          api_key: FEEDBACK_API_KEY,
          turnstile_token: turnstileToken || '',
          website: honeypot, // gizli tuzak alan: insanlar görmez, botlar doldurur
          name: formData.name,
          email: formData.email,
          subject: `[${formData.type.toUpperCase()}] ${formData.subject}`,
          message: formData.message,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.status === 'success') {
        setIsSuccess(true);
        setFormData({ type: '', name: '', email: '', subject: '', message: '' });
      } else {
        setErrorMsg(response.status === 429 ? txt.rateLimited
          : /turnstile/i.test(data.message || '') ? txt.verifyFailed
          : txt.apiError);
        if (widgetId.current !== null && window.turnstile) window.turnstile.reset(widgetId.current);
      }
    } catch (err) {
      console.error('Feedback error:', err);
      setErrorMsg(txt.apiError);
    }

    setIsSubmitting(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsSuccess(false);
    setErrorMsg('');
    setHoneypot('');
    setFormData({ type: '', name: '', email: '', subject: '', message: '' });
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'bug': return <Bug className="h-4 w-4 text-red-500" />;
      case 'feature': return <Sparkles className="h-4 w-4 text-purple-500" />;
      case 'improvement': return <Lightbulb className="h-4 w-4 text-yellow-500" />;
      case 'other': return <MoreHorizontal className="h-4 w-4 text-gray-500" />;
      default: return null;
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 sharp
                   bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.14em]
                   shadow-lg shadow-primary/25 hover:shadow-primary/40 border border-primary/50
                   hover:scale-105 transition-all duration-300"
        data-testid="feedback-button"
      >
        <MessageCircle className="h-4 w-4" />
        <span className="hidden sm:inline">{txt.feedbackButton}</span>
      </button>

      {/* Modal Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          {/* Modal Content */}
          <div className="w-full max-w-md bg-card border border-border sharp shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {!isSuccess ? (
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
                  <h3 className="text-base font-mono uppercase tracking-[0.14em] flex items-center gap-2">
                    <MessageCircle className="h-4 w-4 text-primary" />
                    {txt.title}
                  </h3>
                  <button
                    onClick={handleClose}
                    className="p-1 sharp hover:bg-muted transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                  {/* Type */}
                  <div className="space-y-2">
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{txt.type}</Label>
                    <Select 
                      value={formData.type} 
                      onValueChange={(value) => setFormData({...formData, type: value})}
                      required
                    >
                      <SelectTrigger className="sharp">
                        <SelectValue placeholder={txt.selectType} />
                      </SelectTrigger>
                      <SelectContent className="sharp">
                        <SelectItem value="bug" className="sharp">
                          <div className="flex items-center gap-2">
                            <Bug className="h-3.5 w-3.5 text-red-500" />
                            <span className="text-xs">{txt.typeBug}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="feature" className="sharp">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                            <span className="text-xs">{txt.typeFeature}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="improvement" className="sharp">
                          <div className="flex items-center gap-2">
                            <Lightbulb className="h-3.5 w-3.5 text-yellow-500" />
                            <span className="text-xs">{txt.typeImprovement}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="other" className="sharp">
                          <div className="flex items-center gap-2">
                            <MoreHorizontal className="h-3.5 w-3.5 text-gray-500" />
                            <span className="text-xs">{txt.typeOther}</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Name & Email Row */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{txt.name}</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder={txt.namePlaceholder}
                        className="sharp text-sm"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{txt.email}</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder={txt.emailPlaceholder}
                        className="sharp text-sm"
                        required
                      />
                    </div>
                  </div>

                  {/* Subject */}
                  <div className="space-y-2">
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{txt.subject}</Label>
                    <Input
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      placeholder={txt.subjectPlaceholder}
                      className="sharp text-sm"
                      maxLength={255}
                      required
                    />
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{txt.message}</Label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder={txt.messagePlaceholder}
                      className="sharp text-sm"
                      maxLength={2000}
                      rows={4}
                      required
                    />
                    <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{txt.messageHelp}</p>
                  </div>

                  {/* Honeypot: ekranda ve ekran okuyucuda görünmez; dolu gelirse sunucu mesajı atar */}
                  <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
                    <label>Website<input type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} /></label>
                  </div>

                  {/* Turnstile */}
                  <div ref={turnstileRef} className="min-h-[65px]" />

                  {errorMsg && (
                    <p className="text-xs text-destructive border border-destructive/30 bg-destructive/5 px-3 py-2 sharp" role="alert">{errorMsg}</p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleClose}
                      className="flex-1 sharp font-mono text-xs uppercase tracking-wider"
                    >
                      <X className="h-3.5 w-3.5 mr-2" />
                      {txt.cancel}
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting || !formData.type}
                      className="flex-1 sharp font-mono text-xs uppercase tracking-wider bg-primary hover:bg-primary/90"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                          {txt.sending}
                        </>
                      ) : (
                        <>
                          <Send className="h-3.5 w-3.5 mr-2" />
                          {txt.send}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              /* Success State */
              <div className="p-8 text-center">
                <div className="mx-auto w-14 h-14 sharp bg-primary/10 border border-primary/30 flex items-center justify-center mb-4">
                  <CheckCircle className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-lg font-mono uppercase tracking-wider mb-2">{txt.successTitle}</h3>
                <p className="text-sm text-muted-foreground mb-6">{txt.successMessage}</p>
                <Button
                  onClick={handleClose}
                  className="sharp font-mono text-xs uppercase tracking-wider bg-primary hover:bg-primary/90"
                >
                  <X className="h-3.5 w-3.5 mr-2" />
                  {txt.close}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
